import { useMemo, useState } from 'react';
import { Users as UsersIcon, Plus } from 'lucide-react';
import { toast } from 'react-toastify';
import RightDrawer from '@/shared/components/form/RightDrawer';
import PageBreadcrumb from '@/shared/PageBreadCrumb';
import UserForm from '../components/UserForm';
import UserActivityDrawer from '../components/UserActivityDrawer';
import LoginOtpModal from '../components/LoginOtpModal';
import { buildUserColumns, ToggleStatusCell } from '../components/userColumns';
import { useUsersQuery, useRolesForDdl, useUserMutations } from '../hooks/useUsers';
import { useAuth } from '@/context/AuthContext';
import { DataTableShell } from '@/components/data-table';
import type { ActiveFilterChip } from '@/components/data-table';
import type { User, CreateUserRequest, UpdateUserRequest } from '@/types/user';

const DEPARTMENTS = ['OMS', 'AMS', 'EPC'] as const;

export default function Users() {
  const [search, setSearch] = useState('');
  const [roles, setRoles] = useState<string[]>([]);
  const [department, setDepartment] = useState<'OMS' | 'AMS' | 'EPC' | null>(null);
  const [status, setStatus] = useState<'active' | 'inactive' | null>('active');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortField, setSortField] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const { hasPermission } = useAuth();

  const [openForm, setOpenForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [activityUser, setActivityUser] = useState<User | null>(null);
  const [otpUser, setOtpUser] = useState<User | null>(null);

  const { data: roleList = [] } = useRolesForDdl();
  const { users, totalDocs, isFetching } = useUsersQuery({
    page, pageSize, search, roles, department, status, sortField, sortOrder,
  });
  const { createUser, updateUser, toggleStatus } = useUserMutations();

  const resetPage = () => setPage(1);

  const handleSave = async (data: any) => {
    try {
      if (editingUser?.id) {
        await updateUser.mutateAsync({ id: editingUser.id, data: data as UpdateUserRequest });
      } else {
        await createUser.mutateAsync(data as CreateUserRequest);
      }
      setEditingUser(null);
      setOpenForm(false);
      toast.success(editingUser ? 'User updated' : 'User created');
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to save user');
      throw e;
    }
  };

  const roleOptions = useMemo(
    () => roleList.map((r) => ({ label: r.name, value: r.id || r._id || '' })),
    [roleList],
  );

  const activeFilters = useMemo<ActiveFilterChip[]>(() => {
    const chips: ActiveFilterChip[] = [];
    roles.forEach((rid) => {
      const label = roleOptions.find((o) => o.value === rid)?.label ?? rid;
      chips.push({ key: 'roles', value: rid, display: `Role: ${label}` });
    });
    if (department) chips.push({ key: 'department', value: department, display: `Dept: ${department}` });
    if (status) chips.push({ key: 'status', value: status, display: `Status: ${status}` });
    return chips;
  }, [roles, department, status, roleOptions]);

  const columns = useMemo(
    () =>
      buildUserColumns({
        roles: roleList,
        onEdit: (u) => { setEditingUser(u); setOpenForm(true); },
        onActivity: (u) => setActivityUser(u),
        toggleCell: (u) => (
          <ToggleStatusCell user={u} onConfirm={async () => { await toggleStatus.mutateAsync(u._id ?? u.id ?? ''); }} />
        ),
        ...(hasPermission('userManagement', 'generate') ? { onLoginOtp: (u: User) => setOtpUser(u) } : {}),
      }),
    [roleList, toggleStatus, hasPermission],
  );

  return (
    <div className="mx-auto max-w-9xl space-y-6">
      <PageBreadcrumb pageTitle="User Management" />

      <DataTableShell<User>
        title="User Management"
        subtitle={`${totalDocs} users`}
        icon={<UsersIcon size={20} />}
        primaryAction={{
          label: 'Add User',
          icon: <Plus size={16} />,
          onClick: () => { setEditingUser(null); setOpenForm(true); },
        }}
        columns={columns}
        data={users}
        rowKey={(u) => u._id ?? u.id ?? ''}
        loading={isFetching}
        emptyState={{
          title: 'No users found',
          description: activeFilters.length || search
            ? 'Try adjusting your search or filters.'
            : 'Get started by adding your first user.',
        }}
        toolbar={{
          search: { value: search, onChange: (v) => { setSearch(v); resetPage(); }, placeholder: 'Search name, email, mobile…' },
          filters: [
            { key: 'roles', label: 'Role', mode: 'multiple', options: roleOptions, value: roles, onChange: (v) => { setRoles((v as string[]) ?? []); resetPage(); } },
            { key: 'department', label: 'Department', options: DEPARTMENTS.map((d) => ({ label: d, value: d })), value: department, onChange: (v) => { setDepartment((v as any) || null); resetPage(); } },
            { key: 'status', label: 'Status', options: [{ label: 'Active', value: 'active' }, { label: 'Inactive', value: 'inactive' }], value: status, onChange: (v) => { setStatus((v as any) || null); resetPage(); } },
          ],
          activeFilters,
          onClearFilter: (chip) => {
            if (chip.key === 'roles') setRoles((prev) => prev.filter((r) => r !== chip.value));
            if (chip.key === 'department') setDepartment(null);
            if (chip.key === 'status') setStatus(null);
            resetPage();
          },
          onClearAll: () => { setRoles([]); setDepartment(null); setStatus(null); resetPage(); },
        }}
        pagination={{
          page, pageSize, total: totalDocs,
          onChange: (p, ps) => { setPage(p); setPageSize(ps); },
        }}
        sortField={sortField}
        sortOrder={sortOrder}
        onSortChange={(field, order) => {
          setSortField(field ?? 'createdAt');
          setSortOrder(order ?? 'desc');
          resetPage();
        }}
      />

      <RightDrawer open={openForm} onClose={() => { setOpenForm(false); setEditingUser(null); }} size="60">
        <div className="p-1">
          <UserForm editingUser={editingUser} onAdd={handleSave} onClose={() => { setOpenForm(false); setEditingUser(null); }} />
        </div>
      </RightDrawer>

      <UserActivityDrawer
        open={!!activityUser}
        onClose={() => setActivityUser(null)}
        userId={activityUser?._id ?? activityUser?.id ?? ''}
        userName={activityUser?.name ?? ''}
      />

      {otpUser && <LoginOtpModal user={otpUser} open onClose={() => setOtpUser(null)} />}
    </div>
  );
}
